import { useCallback, useEffect, useState } from 'react'
import { Download, ScrollText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { exportAuditLogs, fetchAuditActions, fetchAuditLogUsers, fetchAuditLogs } from '@/lib/api/auditLogs'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

function formatTimestamp(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

function summarizeChanges(log) {
  const values = log.new_values ?? log.old_values
  if (!values || Object.keys(values).length === 0) {
    return '—'
  }
  return Object.entries(values)
    .slice(0, 3)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ')
}

export function AuditLogPage({ embedded = false }) {
  const [users, setUsers] = useState([])
  const [actions, setActions] = useState([])
  const [logs, setLogs] = useState([])
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 25, total: 0 })
  const [filters, setFilters] = useState({
    user_id: '',
    action: '',
    date_from: '',
    date_to: '',
  })
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true)
    setError('')
    try {
      const params = { page, per_page: 25 }
      if (filters.user_id) params.user_id = Number(filters.user_id)
      if (filters.action) params.action = filters.action
      if (filters.date_from) params.date_from = filters.date_from
      if (filters.date_to) params.date_to = filters.date_to

      const result = await fetchAuditLogs(params)
      setLogs(result.logs)
      setMeta(result.meta)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to load audit logs.')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    let active = true

    async function init() {
      try {
        const [userItems, actionItems] = await Promise.all([fetchAuditLogUsers(), fetchAuditActions()])
        if (!active) return
        setUsers(userItems)
        setActions(actionItems)
      } catch (err) {
        if (!active) return
        setError(err?.response?.data?.message ?? 'Unable to load audit filters.')
      }
    }

    init()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    loadLogs(1)
  }, [loadLogs])

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }))
  }

  async function handleExport() {
    setExporting(true)
    setError('')
    try {
      const params = {}
      if (filters.user_id) params.user_id = Number(filters.user_id)
      if (filters.action) params.action = filters.action
      if (filters.date_from) params.date_from = filters.date_from
      if (filters.date_to) params.date_to = filters.date_to
      await exportAuditLogs(params)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to export audit logs.'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className={embedded ? 'space-y-4' : 'mx-auto max-w-[1440px] px-4 py-8 lg:px-8'}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        {embedded ? (
          <p className="text-sm font-medium">Audit activity</p>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <ScrollText className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Audit log</h1>
              <p className="text-sm text-muted-foreground">
                Security and change history for your tenant.
              </p>
            </div>
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={exporting || loading}
          onClick={handleExport}
          className="gap-2"
        >
          <Download className="h-4 w-4" aria-hidden />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      <div className="card-surface mb-4 grid gap-4 p-4 md:grid-cols-4">
        <div>
          <label htmlFor="audit-user" className="mb-1.5 block text-sm font-medium">
            User
          </label>
          <select
            id="audit-user"
            value={filters.user_id}
            onChange={(e) => updateFilter('user_id', e.target.value)}
            className={cn(
              'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          >
            <option value="">All users</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="audit-action" className="mb-1.5 block text-sm font-medium">
            Action
          </label>
          <input
            id="audit-action"
            list="audit-action-options"
            value={filters.action}
            onChange={(e) => updateFilter('action', e.target.value)}
            placeholder="e.g. auth.login"
            className={cn(
              'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
              'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          />
          <datalist id="audit-action-options">
            {actions.map((action) => (
              <option key={action} value={action} />
            ))}
          </datalist>
        </div>

        <div>
          <label htmlFor="audit-from" className="mb-1.5 block text-sm font-medium">
            From
          </label>
          <input
            id="audit-from"
            type="date"
            value={filters.date_from}
            onChange={(e) => updateFilter('date_from', e.target.value)}
            className={cn(
              'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          />
        </div>

        <div>
          <label htmlFor="audit-to" className="mb-1.5 block text-sm font-medium">
            To
          </label>
          <input
            id="audit-to"
            type="date"
            value={filters.date_to}
            onChange={(e) => updateFilter('date_to', e.target.value)}
            className={cn(
              'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          />
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        {error ? (
          <p className="p-6 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : loading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading audit entries…</p>
        ) : logs.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No audit entries match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-border/60 bg-muted/30 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">When</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                  <th className="px-4 py-3 font-semibold">User</th>
                  <th className="px-4 py-3 font-semibold">Branch</th>
                  <th className="px-4 py-3 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-border/40 last:border-0">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {formatTimestamp(log.created_at)}
                    </td>
                    <td className="px-4 py-3 font-medium">{log.action}</td>
                    <td className="px-4 py-3">{log.user?.name ?? 'System'}</td>
                    <td className="px-4 py-3">{log.branch?.name ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{summarizeChanges(log)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-4 py-3">
          <p className="text-xs text-muted-foreground">
            {meta.total} entries · page {meta.current_page} of {meta.last_page}
            {' · '}Export includes current filters (up to 10,000 rows)
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={meta.current_page <= 1 || loading}
              onClick={() => loadLogs(meta.current_page - 1)}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={meta.current_page >= meta.last_page || loading}
              onClick={() => loadLogs(meta.current_page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
